/**
 * Schedule Utilities
 * Helper functions for recurring job schedule calculations
 */

/**
 * Calculate duration in minutes between two time strings
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {number|null} Duration in minutes, or null if invalid
 */
export function calculateDurationMinutes(startTime, endTime) {
  if (!startTime || !endTime) return null;

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
    return null;
  }

  let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);

  // Handle overnight shifts (e.g., 22:00 - 06:00)
  if (durationMinutes < 0) {
    durationMinutes += 24 * 60;
  }

  return durationMinutes;
}

/**
 * Format duration in minutes to human-readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration (e.g., "3h 30m", "2h", "45m")
 */
export function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '';
  if (minutes === 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Convert duration in minutes to decimal hours
 * @param {number} minutes - Duration in minutes
 * @returns {number} Duration in decimal hours
 */
export function minutesToHours(minutes) {
  if (!minutes) return 0;
  return Math.round((minutes / 60) * 100) / 100;
}

/**
 * Normalize schedule data from old format to new format
 * Provides backward compatibility for existing jobs
 *
 * Old format:
 * { pattern, days, time, customTimes: { mon: '09:00' } }
 *
 * New format:
 * { pattern, days, startTime, endTime, customSchedule: { mon: { startTime, endTime } } }
 *
 * @param {object} schedule - Schedule data (old or new format)
 * @returns {object} Normalized schedule in new format
 */
export function normalizeScheduleData(schedule) {
  if (!schedule) return null;

  // Already in new format (has startTime or customSchedule)
  if (schedule.startTime !== undefined || schedule.customSchedule !== undefined) {
    return schedule;
  }

  // Convert old format to new format
  if (schedule.pattern === 'fixed') {
    return {
      ...schedule,
      startTime: schedule.time || '09:00',
      endTime: null, // Unknown duration from old format
      _legacyFormat: true
    };
  }

  if (schedule.pattern === 'custom') {
    const customSchedule = {};
    Object.entries(schedule.customTimes || {}).forEach(([day, time]) => {
      customSchedule[day] = {
        startTime: time,
        endTime: null // Unknown duration from old format
      };
    });
    return {
      ...schedule,
      customSchedule,
      _legacyFormat: true
    };
  }

  return schedule;
}

/**
 * Check if schedule is in the new format with time ranges
 * @param {object} schedule - Schedule data
 * @returns {boolean} True if using new format with start/end times
 */
export function hasTimeRanges(schedule) {
  if (!schedule) return false;

  if (schedule.pattern === 'fixed') {
    return !!(schedule.startTime && schedule.endTime);
  }

  if (schedule.pattern === 'custom' && schedule.customSchedule) {
    return Object.values(schedule.customSchedule).some(
      day => day?.startTime && day?.endTime
    );
  }

  return false;
}

/**
 * Get time range for a specific day from schedule
 * @param {object} schedule - Schedule data
 * @param {string} dayKey - Day key (e.g., 'mon', 'tue')
 * @returns {object} { startTime, endTime, duration } or null
 */
export function getScheduleForDay(schedule, dayKey) {
  if (!schedule || !dayKey) return null;

  const normalized = normalizeScheduleData(schedule);

  if (normalized.pattern === 'fixed') {
    const startTime = normalized.startTime;
    const endTime = normalized.endTime;
    const duration = calculateDurationMinutes(startTime, endTime);
    return { startTime, endTime, duration };
  }

  if (normalized.pattern === 'custom' && normalized.customSchedule?.[dayKey]) {
    const daySchedule = normalized.customSchedule[dayKey];
    const startTime = daySchedule.startTime;
    const endTime = daySchedule.endTime;
    const duration = calculateDurationMinutes(startTime, endTime);
    return { startTime, endTime, duration };
  }

  return null;
}

/**
 * Calculate total weekly duration from schedule
 * @param {object} schedule - Schedule data with time ranges
 * @returns {number} Total minutes per week
 */
export function calculateWeeklyDuration(schedule) {
  if (!schedule || !schedule.days?.length) return 0;

  let totalMinutes = 0;

  if (schedule.pattern === 'fixed') {
    const duration = calculateDurationMinutes(schedule.startTime, schedule.endTime);
    if (duration) {
      totalMinutes = duration * schedule.days.length;
    }
  } else if (schedule.pattern === 'custom' && schedule.customSchedule) {
    schedule.days.forEach(dayKey => {
      const daySchedule = schedule.customSchedule[dayKey];
      if (daySchedule) {
        const duration = calculateDurationMinutes(daySchedule.startTime, daySchedule.endTime);
        if (duration) {
          totalMinutes += duration;
        }
      }
    });
  }

  return totalMinutes;
}
